<script>
  import SchedulerColumn from '@components/scheduler/scheduler-column.svelte'
  import SegmentedControl from '@components/shared/segmented-control.svelte'
  import FormLabel from '@components/shared/form-label.svelte'
  import { get } from '@lib/requests'
  import { format, addDays, subDays, isToday, isAfter, isFuture, differenceInMinutes } from 'date-fns'
  import { fr } from 'date-fns/locale'
  import { delay, showAlert } from '@lib/utils'
  import { onMount } from 'svelte'

  let {
    date = $bindable(new Date().toISOString().split('T')[0]),
    clinic_id,
    vet_id = $bindable(),
    start_time = $bindable(),
    end_time = $bindable(),
    slotUnavailable = $bindable(false),
    blockOperation = $bindable(false),
    duration = $bindable(10),
    durationLocked = false,
    maxVetColumns = null,
    // Side by side with the form fields: compact header, fills the column height and confirms the selected slot in a panel
    sideBySide = false,
    // Avec / Sans horaire (the form opens on Sans horaire from the calendar's all-day row)
    timed = $bindable(true),
    // Highlights the vet selects after a submit attempt without a vet
    vetInvalid = false,
    onChange
  } = $props()

  let loading = $state(false)
  let updating = $state(false)
  let data = $state({})
  let container = $state(null)
  let dataInterval = $state(null)
  let selectedInterval = $state(null)

  let vets = $derived(data?.all_vets || [])
  let presences = $derived(data?.presences || [])
  let hiddenVets = $state([])

  let disabledVets = $derived(vets.filter(vet => !presences.find(presence => presence.vet.id === vet.id)))

  let availableVets = $derived(vets.filter(vet => !disabledVets.includes(vet)))

  let vet = $derived(vets.find(vet => vet.id === vet_id))
  let slotSelected = $derived(start_time && end_time)

  // let previousDateDisabled = $derived(isToday(new Date(date)))

  let events = $derived(data?.events ? data.events.map(event => ({
    start: new Date(event.start),
    end: new Date(event.end),
    vet: event.vet
  })) : [])

  $effect(() => {
    if (blockOperation) timed = true
  })

  let formattedDate = $derived(format(date, 'EEEE d MMMM yyyy', { locale: fr }))

  let todayComplete = $derived(isToday(new Date(date)) && isAfter(new Date(), new Date(`${date}T20:00:00`)))

  let visiblePresences = $derived(presences.filter(presence => !hiddenVets.includes(presence.vet.id)))
  let columnsWidth = $state(0)

  // Fit as many slot buttons per row (max 3) as each vet column's width allows
  let gridCols = $derived.by(() => {
    const count = maxVetColumns ? Math.min(visiblePresences.length, maxVetColumns) : visiblePresences.length
    if (!columnsWidth || !count) return count >= 4 ? 1 : count === 3 ? 2 : 3
    const columnWidth = (columnsWidth - 16 * (count - 1)) / count
    return Math.max(1, Math.min(3, Math.floor((columnWidth - 24) / 96)))
  })

  async function fetchData(update = false) {
    update ? updating = true : loading = true
    try {
      const response = await get(`/api/v1/schedulers/date?date=${date}&clinic_id=${clinic_id}`)
      const json = await response.json
      data = json
      if (!availableVets.find(vet => vet.id === vet_id)) {
        vet_id = availableVets.length === 1 ? availableVets[0].id : null
      }
    } catch (error) {
      showAlert("Une erreur est survenue lors de la récupération des créneaux", { type: "error" })
    } finally {
      update ? updating = false : loading = false
    }
  }

  async function checkSlotAvailability() {
    if (!slotSelected || !vet_id) return
    const start = start_time.toISOString()
    const end = end_time.toISOString()
    const response = await get(`/api/v1/schedulers/check_slot?vet_id=${vet_id}&start_time=${start}&end_time=${end}`)
    const json = await response.json
    if (json.error) {
      slotUnavailable = true
    } else {
      slotUnavailable = false
    }
  }

  // Kept separate so that selecting a slot doesn't refetch (and show the loading state)
  $effect(() => {
    if (date) fetchData()
  })

  $effect(() => {
    if (start_time && end_time) timed = true
  })

  function getVetEvents(vet) {
    return events.filter(event => event.vet.id === vet.id)
  }

  function goToToday() {
    date = format(new Date(), 'yyyy-MM-dd')
  }

  function prevDate() {
    date = format(subDays(new Date(date), 1), 'yyyy-MM-dd')
  }

  function nextDate() {
    date = format(addDays(new Date(date), 1), 'yyyy-MM-dd')
  }

  function onSelectSlot({ slot, vet }) {
    vet_id = vet?.id
    start_time = slot.start
    end_time = slot.end
    onChange?.({ slot, vet })
  }

  async function clearSelection() {
    vet_id = null
    start_time = null
    end_time = null
    onChange?.({ slot: null, vet: null })
    await delay(100)
    scrollIntoView()
  }

  export function reset(runCallback = true) {
    clearSelection()
    runCallback && onChange?.({ slot: null, vet: null })
  }

  // Focus whichever vet select is showing (no time, or a calendar slot without a vet), else show the grid
  export function focusVet() {
    const select = [...(container?.querySelectorAll("select[name='vet_id']") || [])].find(select => select.offsetParent)
    select ? select.focus() : scrollIntoView()
  }

  export function scrollIntoView() {
    container?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  $effect(() => {
    if (timed) scrollIntoView()
  })

  $effect(async () => {
    if (vet_id) await checkSlotAvailability()
  })

  $effect(() => {
    if (slotSelected) {
      selectedInterval = setInterval(() => { checkSlotAvailability() }, 10000)
      return () => clearInterval(selectedInterval)
    } else {
      clearInterval(selectedInterval)
    }
  })

  onMount(() => {
    dataInterval = setInterval(() => { fetchData(true) }, 30000)
    return () => clearInterval(dataInterval)
  })

</script>

{#snippet vetSelectOptions()}
  <option value=""></option>

  <!-- If the chosen date is today we allow the "emergency" selection of any vet -->
  {#if !isFuture(date)}

    {#if availableVets.length > 0}
      <option value="" disabled>--- Vétérinaires présents ---</option>
      {#each availableVets as vet (vet.id)}
        <option value={vet.id}>{vet.name}</option>
      {/each}
    {/if}

    {#if disabledVets.length > 0}
      <option value="" disabled>--- Autres vétérinaires ---</option>
      {#each disabledVets as vet (vet.id)}
        <option value={vet.id}>{vet.name}</option>
      {/each}
    {/if}

    <!-- Else we only allow the available vets -->
  {:else}
    <option value="" disabled>--- Vétérinaires présents ---</option>
    {#each availableVets as vet (vet.id)}
      <option value={vet.id}>{vet.name}</option>
    {/each}

  {/if}

{/snippet}

<!-- Side by side, the scheduler fills its column's height and the vet columns stretch to fill the rest -->
<div bind:this={container} class:flex={sideBySide} class:flex-col={sideBySide} class:h-full={sideBySide}>

  <section class="form-items" class:hidden={Boolean(slotSelected)}>

    {#if !sideBySide}
      <FormLabel label="Créneau" />
    {/if}

    <div class="flex flex-wrap col-span-12 gap-4 items-end mb-6" >

      <div class="flex flex-col">
        <label for="timed">Horaire</label>
        <SegmentedControl options={[{ label: "Avec horaire", value: true }, { label: "Sans horaire", value: false } ]} bind:value={timed} disabled={blockOperation}/>
      </div>

      <!-- Side by side, the date goes on its own full width row above Horaire and Durée -->
      <div class="inline-flex items-end gap-1 flex-1 min-w-[14rem]" class:order-first={sideBySide} class:basis-full={sideBySide}>

        <div class="flex flex-col flex-1">
          <label for="date">Date</label>
          <input type="date" id="date" bind:value={date} class="input w-full" disabled={slotSelected} />
        </div>

        <button type="button" class="button no-bg h-[2.5rem] items-center" onclick={prevDate} aria-label="Date précédente" title="Date précédente">
          <i class="fal fa-angle-left"></i>
        </button>
        <button type="button" class="button no-bg h-[2.5rem] items-center" onclick={nextDate} aria-label="Date suivante" title="Date suivante">
          <i class="fal fa-angle-right"></i>
        </button>
        <button type="button" class="button no-bg h-[2.5rem] items-center" onclick={goToToday} aria-label="Aujourd'hui" title="Aujourd'hui" disabled={isToday(new Date(date))}>
          <i class="fal fa-calendar"></i>
        </button>
      </div>

      <div class:hidden={!timed} class:ml-auto={sideBySide}>
        <label for="duration">Durée</label>
        <select name="duration" id="duration" bind:value={duration} class="select min-w-[10rem]" disabled={slotSelected || durationLocked} title={durationLocked ? "Durée définie par la catégorie" : null}>
          <option value={10}>10 minutes</option>
          <option value={20}>20 minutes</option>
          <option value={30}>30 minutes</option>
          <option value={40}>40 minutes</option>
          <option value={50}>50 minutes</option>
          <option value={60}>1 heure</option>
          <option value={90}>1.5 heures</option>
          <option value={120}>2 heures</option>
          <option value={150}>2.5 heures</option>
          <option value={180}>3 heures</option>
        </select>
      </div>

      <div class="form-item col-span-6" class:hidden={Boolean(timed)} class:flex-1={sideBySide}>
        <label for="vet_id">Vétérinaire</label>
        <select name="vet_id" id="vet_id" class="select {sideBySide ? "w-full" : "min-w-[20rem]"} {vetInvalid ? "border-red-400! focus:ring-red-400!" : ""}" bind:value={vet_id}>
          {@render vetSelectOptions()}
        </select>

      </div>

    </div>

  </section>

  {#if sideBySide && !timed}
    <div class="flex-1 min-h-0 flex items-center justify-center bg-gray-50 border border-dashed border-gray-200 rounded-md p-6">
      <p class="text-center text-gray-500 text-smaller first-letter:uppercase">{formattedDate}{vet ? ` - ${vet.name}` : ""}</p>
    </div>
  {/if}

  {#if timed}

    {#if slotSelected}
      {#if sideBySide}

        <!-- Side by side, the selected slot is confirmed in a panel filling the column (same style as the no-time note) -->
        <div class="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 bg-gray-50 border border-dashed border-gray-200 rounded-md p-6 text-center text-smaller text-gray-500">

          {#if slotUnavailable}
            <div class="bg-red-100/80 px-4 py-2 mb-2 border border-red-200 rounded text-sm text-red-800">Ce créneau n'est plus disponible</div>
          {/if}

          {#if vet}
            <div class="flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-{vet.color}/80"></span>
              <span class="font-semibold text-gray-700">{vet.name}</span>
              {#if blockOperation}<span>indisponible</span>{/if}
            </div>
          {:else if !vet_id && data?.all_vets}
            <!-- Slots picked on the calendar may not have a vet yet (only shown once the vets have loaded, to avoid a flash) -->
            <div class="form-item w-72 mb-2 text-left">
              <label for="slot_vet_id">Sélectionner un vétérinaire *</label>
              <select name="vet_id" id="slot_vet_id" class="select w-full {vetInvalid ? "border-red-400! focus:ring-red-400!" : ""}" bind:value={vet_id}>
                {@render vetSelectOptions()}
              </select>
            </div>
          {/if}

          <p class="first-letter:uppercase">{format(new Date(start_time), 'EEEE d MMMM yyyy', { locale: fr })}</p>
          <p class="text-lg text-gray-700">
            {format(new Date(start_time), 'HH:mm')} – {format(new Date(end_time), 'HH:mm')}
            <!-- <span class="text-smaller text-gray-400">({differenceInMinutes(new Date(end_time), new Date(start_time))} min)</span> -->
          </p>

          <button type="button" class="flex items-center gap-1 text-xs text-gray-500 underline hover:text-gray-700" onclick={clearSelection}>
            <span>Supprimer</span>
          </button>

        </div>

      {:else}

      <FormLabel label="Créneau sélectionné" />

      {#if slotUnavailable}
        <div class="bg-red-100/80 p-4 border border-red-200 rounded text-sm text-red-800 flex items-center gap-1">
          <span>Ce créneau n'est plus disponible</span>
        </div>
      {/if}

      <!-- The slot selected information block -->
      <div class="form-item col-span-12 mt-4">

        <div class="bg-blue-100/80 p-4 border border-blue-200 rounded text-sm relative text-blue-800 flex items-center gap-1" class:bg-gray-100={blockOperation} class:text-gray-500={blockOperation} class:border-gray-200={blockOperation}>
          {#if vet}
            {#if !blockOperation}<span>Avec</span>{/if}
            <span class="italic font-semibold">{vet.name}</span>
          {/if}
          {#if blockOperation}
            <span>indisponible</span>
          {/if}
          <span>{vet ? "le" : "Le"}</span>
          <span class="underline">{format(start_time, 'EEEE d MMMM yyyy', { locale: fr })}</span>
          <span>de</span>
          <span class="italic">{format(start_time, 'HH:mm')} à {format(end_time, 'HH:mm')}</span>
          <button type="button" title="Annuler la sélection" aria-label="Annuler la sélection" class="ml-auto" onclick={clearSelection}>
            <i class="fal fa-times text-gray-600"></i>
          </button>

        </div>

      </div>

      {#if !vet}

        <div class="form-item col-span-12 mt-6">

          <label for="vet_id">Sélectionner un vétérinaire *</label>

          <select name="vet_id" id="vet_id" class="select w-full {vetInvalid ? "border-red-400! focus:ring-red-400!" : ""}" bind:value={vet_id}>
            {@render vetSelectOptions()}
          </select>

        </div>
      {/if}

      {/if}
    {/if}

    {#if !slotSelected}

      <section class="{sideBySide ? "flex-1 min-h-0" : "h-[30rem]"} overflow-y-auto relative">

      {#if !sideBySide}
        <h5 class="text-center text-gray-500 font-medium text-smaller mb-4">{formattedDate}</h5>
      {/if}

      {#if loading}
        <div class="flex items-center justify-center h-full bg-gray-50 border border-gray-100">
          <p class="text-center text-gray-500 text-smaller">Chargement des créneaux...</p>
        </div>

      {:else if todayComplete}
        <div class="flex items-center justify-center h-full bg-gray-50 border border-gray-100">
          <p class="text-center text-gray-500 text-smaller">Les créneaux de ce jour sont passés</p>
        </div>

      {:else if presences.length > 0}

        <!-- Vet columns side by side, or wrapped in rows of maxVetColumns -->
        <div class="{maxVetColumns ? `grid grid-cols-${maxVetColumns} gap-4` : "flex gap-4"} {sideBySide ? "h-full auto-rows-[100%]" : ""}" class:opacity-80={updating} bind:clientWidth={columnsWidth}>

          {#each visiblePresences as presence (presence.vet.id)}
            <SchedulerColumn vet={presence.vet} {duration} {date} events={getVetEvents(presence.vet)} period={presence.period} {gridCols} fill={sideBySide} onSelect={onSelectSlot} />
          {/each}

        </div>

      {:else}

        <div class="flex items-center justify-center h-full bg-gray-50 border border-gray-100">
          <p class="text-center text-gray-500 text-smaller">Aucune présence trouvée pour ce jour</p>
        </div>

      {/if}

    </section>

    {/if}

  {/if}

</div>
